/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */
import { useSpringRef, useTransition, animated } from '@react-spring/web';
import { Bug, CurlyBraces, PlayCircle, FileText, Milestone } from 'lucide-react';
import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { useSocket } from 'socket.io-react-hook';

import { BugReport } from './BugReport';
import { InstructionsTab } from './InstructionsTab';
import VideoPlayer from './Player/VideoPlayer';
import { StepContext } from './RunViewer';
import { Tabs } from './Tabs';
import ThreeWayToggle from './ThreeWayToggle';
import { TriageTab } from './TriageTab';

import { RunViewerData } from '@/commons/commons';
import { Action, GotoAction, MutatesVariables, RESULT, Step, WebAgentAction } from '@/commons/data';
import { RunStatus } from '@/commons/protocols';
import { StatusIcon } from '@/components/atoms/StatusIcon';
import ActionBugs from '@/components/runViewer/ActionBugs';
import RunViewerSteps from '@/components/runViewer/RunViewerSteps';
import { stripProtocol } from '@/lib/utils';
import { useGet, useUser } from '@/net/commons';
import { cn } from '@/utils';

// BUFFER is the amount of time we remove to the action start in order to
// seek a bit earlier in the video when we click on a step.
const BUFFER = 1500;

// FW_BUFFER are deltas before and after the action to skip inactivity
const FW_BUFFER_BEFORE = 750;
const FW_BUFFER_AFTER = 500;

// NEXT_STEP_DELTA is the amount of time we remove to the action start to update the
// active step when the video is playing
const NEXT_STEP_DELTA = 1000;

// We do skip inactive sections that are under INACTIVE_SECTION_MIN_DURATION ms
// This is needed for PW execution, where the inactive sections are very short and the player is
// flaky when playing them
const INACTIVE_SECTION_MIN_DURATION = 2000;

export type Inactivity = 'off' | 'fast-forward' | 'skip';

export const useRunViewerVideoPlayer = ({
    runData,
    projectSlug,
    suiteSlug,
    executionId,
    runId,
    videoPlayerClassName,
    updateStepRankCallback,
    updateActionCallback,
}: {
    runData: RunViewerData | undefined;
    projectSlug: string | undefined;
    suiteSlug: string | undefined;
    executionId: string | undefined;
    runId: string | undefined;
    videoPlayerClassName?: string;
    updateStepRankCallback?: (stepRank: number) => void;
    updateActionCallback?: (actionRank: number) => void;
}) => {
    const videoPlayerRef = useRef<HTMLVideoElement | null>(null);
    const { data: instructions, mutate: reloadInstructions } = useGet<{ instructions: string }>(
        runData?.story.slug
            ? `/bff/project/${projectSlug}/suite/${suiteSlug}/story/slug/${runData.story.slug}/instructions`
            : null,
    );

    const [videoPlayerReady, setVideoPlayerReady] = useState(false);
    const [chapters, setChapters] = useState<{ time: number; result: RESULT }[]>([]);
    const [status, setStatus] = useState<undefined | RunStatus | 'running-testagent'>(undefined);
    const [sessionStatus, setSessionStatus] = useState('');
    const [skipInactivity, setSkipInactivity] = useState<Inactivity>('fast-forward');
    const [precomputedVariables, setPrecomputedVariables] = useState<{ [key: string]: string }>({});
    const [debugPageId, setDebugPageId] = useState<string | null>(null);
    const [stepsFromSocket, setStepsFromSocket] = useState<Step[] | undefined>(undefined);

    const [currentUrl, setCurrentUrl] = useState<string | undefined>(undefined);
    const [attempt, setAttempt] = useState<number>(1);
    const [highlightedStepRank, setHighlightedStepRank] = useState<number | undefined>(undefined);
    const [highlightedAction, setHighlightedAction] = useState<number | undefined>(undefined);
    const [debugMode, setDebugMode] = useState(false);
    const toggleDebugMode = () => setDebugMode(!debugMode);
    const [hidePlaywright, setHidePlaywright] = useState(false);
    const toggleHidePlaywright = () => setHidePlaywright(!hidePlaywright);

    const stepResultsFromBack = runData?.runAttempt?.steps;
    const { user } = useUser();
    const isAdmin = user?.isSuperUser;
    const isImpersonated = user?.impersonatedBy;
    const hasAdminPermissions = isAdmin || isImpersonated;

    const wsProtocol = process.env.NODE_ENV !== 'development' ? 'wss' : 'ws';
    const wsUrl =
        process.env.NODE_ENV !== 'development' || import.meta.env.VITE_CLUSTER_SETUP === 'true'
            ? `${stripProtocol(import.meta.env.VITE_CLUSTER_URL)}/${runData?.run?.sessionUuid}/debug/devtools/page/${debugPageId}`
            : `localhost:9222/devtools/page/${debugPageId}`;

    useEffect(() => {
        const variablesBuffer: Record<string, string> = {};
        for (const step of stepResultsFromBack ?? []) {
            for (const action of step?.actions ?? {}) {
                try {
                    if (action.variables) {
                        for (const [key, value] of Object.entries(action.variables)) {
                            if (value !== undefined) {
                                variablesBuffer[key] = value;
                            }
                        }
                    }
                    if ('variablesAfter' in action && 'variablesBefore' in action) {
                        const variablesAfter = (action as MutatesVariables).variablesAfter;
                        if (variablesAfter && variablesAfter.length > 0) {
                            for (const [key, value] of variablesAfter) {
                                if (value !== undefined) {
                                    variablesBuffer[key] = value;
                                }
                            }
                        }
                    }
                } catch (e) {}
            }
        }
        setPrecomputedVariables(variablesBuffer);
    }, [stepResultsFromBack]);

    const videoTimeOffset = useMemo(() => runData?.runAttempt?.videoTimeOffset || 0, [runData]);

    /**
     * Adjusts the timestamps of an action to add some delay before the action and
     * clearly separate actions.
     * videoTimeOffset is the time of the first screenshot in the trace relative to the
     * agent start time, so we need to subtract it from the agent timestamps to get the
     * timestamps relative to the video.
     * BUFFER is the amount of time we remove to the action start in order to
     * seek a bit earlier in the video when we click on a step.
     */
    const getAdjustedTimestamps = (
        startTime: number,
        beforeActionTimeStamp?: number,
        afterActionTimeStamp?: number,
    ): { before: number; after: number } => {
        return {
            before:
                (beforeActionTimeStamp && beforeActionTimeStamp !== -1 ? beforeActionTimeStamp : startTime) -
                videoTimeOffset -
                BUFFER,
            after:
                (afterActionTimeStamp && afterActionTimeStamp !== -1
                    ? afterActionTimeStamp
                    : beforeActionTimeStamp && beforeActionTimeStamp !== -1
                      ? beforeActionTimeStamp + BUFFER
                      : startTime + BUFFER) -
                videoTimeOffset -
                BUFFER,
        };
    };

    /**
     * Gets the before and after timestamps for an action
     */
    const getActionTimestamps = (action: Action): { before: number; after: number } => {
        switch (action.kind) {
            case 'execPlaywright':
            case 'wait':
            case 'pressKey':
            case 'aiExtractInfo':
            case 'readEmail':
            case 'readSMS':
            case 'simpleAssertion':
            case 'aiAssertion':
                // general case
                return getAdjustedTimestamps(action.startTime);
            case 'webAgent':
            case 'gotoUrl':
                const typedAction = action as WebAgentAction | GotoAction;
                return getAdjustedTimestamps(
                    typedAction.startTime,
                    typedAction.beforeActionTimeStamp,
                    typedAction.afterActionTimeStamp,
                );
            default:
                // exhaustive check, so TS type checker warns us if we forget to support an action kind
                action.kind satisfies never;
                throw new Error('getActionTimestamps: Unknown action kind');
        }
    };

    const computedTimeStamps = useMemo(() => {
        if (!runData?.runAttempt?.steps) return [];
        const timeStamps = [];
        for (const step of runData.runAttempt.steps) {
            for (const [index, action] of step.actions.entries()) {
                const { before, after } = getActionTimestamps(action);
                timeStamps.push({
                    before,
                    after,
                    stepRank: step.rank,
                    actionIndex: index,
                    url: action.urlAfter,
                    result: action.result,
                });
            }
        }
        return timeStamps;
    }, [runData, videoTimeOffset]);

    const inactiveTimeStamps = useMemo(() => {
        const inactivity = [];
        if (computedTimeStamps.length === 0) return [];
        if (computedTimeStamps.length === 1)
            return [
                {
                    start: Math.max(computedTimeStamps[0].before - FW_BUFFER_BEFORE, 0),
                    end: computedTimeStamps[0].after + FW_BUFFER_AFTER,
                },
            ];

        let previousActionTs = computedTimeStamps[0];
        for (let i = 0; i < computedTimeStamps.length; i += 1) {
            if (i === 0) {
                if (Math.max(previousActionTs.before - FW_BUFFER_BEFORE, 0) > 0) {
                    inactivity.push({ start: 0, end: previousActionTs.before - FW_BUFFER_BEFORE });
                }
            } else {
                if (previousActionTs.after + FW_BUFFER_AFTER < computedTimeStamps[i].before - FW_BUFFER_BEFORE) {
                    inactivity.push({
                        start: previousActionTs.after + FW_BUFFER_AFTER,
                        end: computedTimeStamps[i].before - FW_BUFFER_BEFORE,
                    });
                    previousActionTs = computedTimeStamps[i];
                }
            }

            // Add the end of the video
            if (i === computedTimeStamps.length - 1) {
                inactivity.push({
                    start: computedTimeStamps[i].after + FW_BUFFER_AFTER,
                    end: Infinity,
                });
            }
        }

        // Remove inactive sections that are under INACTIVE_SECTION_MIN_DURATION ms
        return inactivity.filter((i) => i.end - i.start > INACTIVE_SECTION_MIN_DURATION);
    }, [computedTimeStamps]);

    const runStatus = useMemo(() => runData?.run?.status, [runData]);

    useEffect(() => {
        setChapters(
            computedTimeStamps.map((ts) => {
                return { time: ts.before, result: ts.result };
            }),
        );
    }, [computedTimeStamps]);

    const setPlayHeadToAction = (action: Action, extraOffset?: number) => {
        const videoPlayer = videoPlayerRef.current;
        if (videoPlayer) {
            const { before } = getActionTimestamps(action);
            videoPlayer.playbackRate = 1;
            videoPlayer.currentTime = before / 1000 - (extraOffset ?? 0);
            videoPlayer.play();
        }
    };

    const {
        data: videoData,
        isLoading: isVideoLoading,
        mutate,
    } = useGet<{ url: string }>(
        runStatus === 'finished'
            ? `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}/video${
                  // In theory the attempt should never be undefined here, but we never know...
                  runData?.runAttempt?.attempt ? `?attempt=${runData.runAttempt.attempt}` : ''
              }`
            : null,
        { revalidateOnFocus: false },
    );

    const { socket } = useSocket(import.meta.env.VITE_CLUSTER_URL, {
        enabled: status === 'running' || status === 'running-testagent',
        reconnection: true,
        reconnectionAttempts: 5,
        path:
            process.env.NODE_ENV !== 'development' || import.meta.env.VITE_CLUSTER_SETUP === 'true'
                ? `/${runData?.run.sessionUuid}/node/agent`
                : '/agent',
        extraHeaders: {
            'x-heal-kind': 'studio',
        },
    });

    // Handle the user clicking on an action card
    const handleClickOnStepOrAction = (stepRank: number, actionRank?: number) => {
        setHighlightedStepRank(stepRank);
        updateStepRankCallback?.(stepRank);
        setHighlightedAction(actionRank);
        updateActionCallback?.(actionRank ?? 0);
        const action = runData?.runAttempt?.steps[stepRank].actions[actionRank ?? 0];

        action && setPlayHeadToAction(action);
    };

    useEffect(() => {
        if (runData?.run.status !== status) {
            setStatus(runData?.run.status);
        }
        if (runData?.run.status === 'finished' || runData?.run.status === 'failed') {
            setCurrentUrl(runData.story.entryHref);
        }
    }, [runData]);

    useEffect(() => {
        if (socket) {
            socket.on('connect', () => {
                socket.emit('getHistory');
            });

            socket.on('status', ({ status: st, debugPageId: id }) => {
                setSessionStatus(st);
                setDebugPageId(id);
            });

            socket.on('webAgentStarted', ({ attempt: a }) => {
                setStatus('running');
                setStepsFromSocket([]);
                setAttempt(a);
            });

            socket.on('eventsHistory', (eventsHistory) => {
                setStepsFromSocket((previousHistory) => {
                    // If there's already an history this is an noisy message, ignore
                    if (previousHistory !== undefined) {
                        return previousHistory;
                    }

                    // For each step, we only keep the latest stepUpdate
                    const stepUpdatesHistory = eventsHistory
                        .filter((e: any) => e[0] === 'stepUpdate')
                        .map((e: [string, { step: Step }]) => e[1].step);
                    const ranks = stepUpdatesHistory.map((s: Step | undefined) => s?.rank);
                    const res = [];
                    for (const rank of ranks) {
                        res.push(stepUpdatesHistory.filter((s: Step | undefined) => s?.rank === rank).pop());
                    }
                    return res;
                });

                // Update currentURL to latest in history
                const urlHistory = eventsHistory?.filter((e: any) => e?.length > 0 && e[0] === 'navigatedTo');
                if (urlHistory.length > 0) {
                    urlHistory[urlHistory.length - 1][1]?.url &&
                        setCurrentUrl(urlHistory[urlHistory.length - 1][1].url);
                }

                // Determine if the session started with a new attempt
                const maybeStarted = eventsHistory?.filter((e: any) => e[0] === 'webAgentStarted');
                if (maybeStarted.length > 0) {
                    setStatus('running');
                }

                // Determine if web agent session has ended and the attempt
                const maybeEndedEvents = eventsHistory?.filter((e: any) => e[0] === 'webAgentEnded');
                if (maybeEndedEvents.length > 0) {
                    setStatus('running-testagent');
                }

                const maybeSessionStatus = eventsHistory?.filter((e: any) => e[0] === 'status');
                if (maybeSessionStatus.length > 0) {
                    const { status: st, debugPageId: id } = maybeSessionStatus.pop()[1];
                    setSessionStatus(st);
                    setDebugPageId(id);
                }
            });

            socket.on('stepUpdate', ({ step }: { step: Step }) => {
                if (status === 'running' || status === 'queued') {
                    setStepsFromSocket((previousSteps) => {
                        if (previousSteps === undefined || previousSteps.length === 0) return [step];
                        // if step already has a reported result, update it
                        if (previousSteps.map((s) => s?.rank).includes(step?.rank)) {
                            return previousSteps.map((s) => (s?.rank === step?.rank ? step : s));
                        }
                        // Otherwise add the step to
                        return [...previousSteps, step];
                    });
                }
            });

            socket.on('navigatedTo', ({ url }: { url: string }) => {
                setCurrentUrl(url);
            });

            socket.on('webAgentEnded', ({ attempt: a }) => {
                setStatus('running-testagent');
            });
        }

        return () => {
            if (socket) {
                socket.off('connect');
                socket.off('disconnect');
                socket.off('status');
                socket.off('webAgentStarted');
                socket.off('eventsHistory');
                socket.off('stepUpdate');
                socket.off('navigatedTo');
                socket.off('webAgentEnded');
            }
        };
    }, [socket, status]);

    const handleTimeChange = useCallback(
        (target: HTMLVideoElement) => {
            // Throttle the state updates to avoid constant rerenders
            const currentTime = target.currentTime * 1000;

            // Build an array of timestamps, with start the start time of computedTimeStamps, and end the end time of the next timestamp
            const timestamps = computedTimeStamps.map((ts, index) => ({
                start: index === 0 ? 0 : ts.before - NEXT_STEP_DELTA,
                end: computedTimeStamps[index + 1]?.before - NEXT_STEP_DELTA || Infinity,
            }));

            const currentTimestampIndex = timestamps.findIndex(
                (ts) => ts.start <= currentTime && currentTime <= ts.end,
            );

            // Only update URL if it changed
            const newUrl = computedTimeStamps[currentTimestampIndex]?.url;
            if (newUrl && newUrl !== currentUrl) {
                setCurrentUrl(newUrl);
            }

            // Only update highlighted step/action if they changed
            if (currentTimestampIndex !== -1) {
                const newStepRank = computedTimeStamps[currentTimestampIndex].stepRank;
                const newActionIndex = computedTimeStamps[currentTimestampIndex].actionIndex;

                if (newStepRank !== highlightedStepRank) {
                    setHighlightedStepRank(newStepRank);
                    updateStepRankCallback?.(newStepRank);
                }

                if (newActionIndex !== highlightedAction) {
                    setHighlightedAction(newActionIndex);
                    updateActionCallback?.(newActionIndex);
                }
            }

            // Handle playback rate changes
            const inactiveIndex = inactiveTimeStamps
                .filter((i) => i.start >= 0 && i.end >= 0)
                .findIndex((ts) => ts.start <= currentTime && currentTime <= ts.end);

            if (inactiveIndex === -1) {
                if (target.playbackRate !== 1) {
                    target.playbackRate = 1;
                }
                return;
            }

            if (skipInactivity === 'fast-forward') {
                if (target.playbackRate !== 10) {
                    target.playbackRate = 10;
                }
            } else if (skipInactivity === 'skip') {
                const nextTimestampIndex = computedTimeStamps.findIndex((ts) => ts.before - 2000 > currentTime);
                if (nextTimestampIndex !== -1) {
                    target.currentTime = computedTimeStamps[nextTimestampIndex].before / 1000;
                }
            }
        },
        [computedTimeStamps, inactiveTimeStamps, skipInactivity],
    );

    const cancelRun = useCallback(() => {
        if (socket && socket.connected) {
            socket.emit('cancelRun');
        }
    }, [socket]);

    const tabItems = [
        {
            title: 'Replay',
            tabHeader: (
                <div className="flex gap-[8px] py-2 px-[8px] items-center">
                    <PlayCircle size="16" className="text-purple-600" />
                    Replay
                </div>
            ),
            content: (
                <div className="flex flex-col h-full gap-y-[8px]">
                    <div className="flex flex-row marker:items-center justify-between rounded-sm p-2 whitespace-nowrap min-w-0 max-w-full bg-white text-slate-500">
                        <p className="text-small truncate [text-box:trim-both]">
                            {currentUrl || runData?.story.entryHref}
                        </p>
                    </div>
                    <div className="h-full bg-white rounded-[10px] overflow-hidden">
                        {status === 'canceled' ? (
                            <div className="mx-auto h-full flex items-center justify-center gap-1 text-muted-foreground">
                                <StatusIcon status={'canceled'} />
                                <p>The run was canceled</p>
                            </div>
                        ) : status === 'finished' ? (
                            <>
                                <VideoPlayer
                                    src={videoData?.url || ''}
                                    videoPlayerRef={(node: HTMLVideoElement) => {
                                        videoPlayerRef.current = node;
                                        setVideoPlayerReady(true);
                                    }}
                                    autoPlay
                                    chapters={chapters}
                                    className=""
                                    videoPlayerClassName={videoPlayerClassName}
                                    onError={mutate}
                                    handleSeek={handleTimeChange}
                                />
                                <div className="flex flex-col lg:flex-row items-center justify-center gap-x-8 gap-y-2 mt-4">
                                    Inactive sections:{' '}
                                    <ThreeWayToggle defaultValue={skipInactivity} onChange={setSkipInactivity} />
                                </div>
                                <InactivityStatusIndicator inactivity={skipInactivity} />
                            </>
                        ) : status === 'queued' || sessionStatus !== 'sessionReady' ? (
                            <div className="mx-auto h-full flex items-center justify-center gap-1 text-muted-foreground">
                                <StatusIcon status={'queued'} />
                                <p>The run is starting</p>
                            </div>
                        ) : status === 'running-testagent' ? (
                            <div className="mx-auto h-full flex items-center justify-center gap-1 text-muted-foreground">
                                <StatusIcon status={'queued'} />
                                <p>The agent is now analyzing test results and processing the replay video</p>
                            </div>
                        ) : (
                            status === 'running' &&
                            sessionStatus === 'sessionReady' &&
                            debugPageId && (
                                <>
                                    <div>
                                        <div className="vp-container">
                                            <iframe
                                                src={`https://screencast-chi.vercel.app/inspector.html?${wsProtocol}=${wsUrl}`}
                                                className="w-full h-full border-0 pointer-events-none overflow-hidden"
                                                style={{ aspectRatio: 1280 / 720, maxHeight: 'calc(100% - 28px)' }}
                                                title="Inspector"
                                                allowFullScreen
                                            />
                                        </div>
                                    </div>
                                </>
                            )
                        )}
                    </div>
                </div>
            ),
        },
        runData?.result?.report?.feedback
            ? {
                  title: 'Bug Report',
                  tabHeader: (
                      <div className="flex gap-[8px] py-2 px-[8px] items-center">
                          <Bug size="16" className="text-purple-600" />
                          Bug Report
                      </div>
                  ),
                  content: <BugReport report={runData?.result?.report} />,
              }
            : null,
        {
            title: 'Instructions',
            tabHeader: (
                <div className="flex gap-[8px] py-2 px-[8px] items-center">
                    <FileText size="16" className="text-purple-600" />
                    Instructions
                </div>
            ),
            content: (
                <div className="bg-white rounded-lg h-full overflow-auto p-4">
                    <InstructionsTab
                        storySlug={runData?.story?.slug}
                        instructionsData={instructions}
                        onSave={() => {
                            reloadInstructions();
                        }}
                    />
                </div>
            ),
        },
        runData?.run?.variables && Object.keys(runData.run.variables).length > 0
            ? {
                  title: 'Variables',
                  tabHeader: (
                      <div className="flex gap-[8px] py-2 px-[8px] items-center">
                          <CurlyBraces size="16" className="text-purple-600" />
                          Variables
                      </div>
                  ),
                  content: (
                      <div className="bg-white rounded-lg h-full overflow-auto p-4">
                          <div className="px-2 py-3 flex items-center text-slate-700 gap-1 border-b font-medium leading-6">
                              <span className="rounded h-[24px] px-0.5 bg-purple-100 text-purple-900 flex items-center justify-center">
                                  <span className="text-detail">{'{{var}}'}</span>
                              </span>
                              Variables
                          </div>
                          {Object.entries(runData.run.variables).map(([key, value]) => {
                              const definition = value;
                              const actualValue = precomputedVariables[key] ?? definition;
                              return (
                                  <div key={key}>
                                      <div className="py-3 px-2 flex items-center text-purple-400 gap-1 border-b font-medium leading-6">
                                          {key}
                                      </div>
                                      <div className="py-1 px-2">
                                          <div className="font-medium leading-6">Definition:</div>
                                          <div className="">{definition}</div>
                                      </div>
                                      {definition !== actualValue && (
                                          <div className="py-1 px-2">
                                              <div className="font-medium leading-6">Value:</div>
                                              <div className="">{actualValue}</div>
                                          </div>
                                      )}
                                  </div>
                              );
                          })}
                      </div>
                  ),
              }
            : null,
        runData?.triage && hasAdminPermissions
            ? {
                  title: 'Triage',
                  tabHeader: (
                      <div className="flex gap-[8px] py-2 px-[8px] items-center">
                          <Milestone size="16" className="text-purple-600" />
                          Triage
                      </div>
                  ),
                  content: (
                      <div className="bg-white rounded-lg h-full overflow-auto p-4">
                          <TriageTab history={runData.triage.history} conclusion={runData.triage.conclusion} />
                      </div>
                  ),
              }
            : null,
    ].filter((item) => item);

    const stepsComponent = runData ? (
        <StepContext.Provider
            value={{
                runStatus: runData.run.status,
                highlightedStepRank,
                highlightedAction,
                handleClickOnStepOrAction,
            }}
        >
            <div className="flex flex-col h-full overflow-hidden">
                <div className="h-full overflow-auto flex flex-col">
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <p className="font-medium">Steps</p>
                        {hasAdminPermissions && (
                            <div className="flex items-center gap-2">
                                <button title="Toggle Debug Mode" className="shrink-0" onClick={toggleDebugMode}>
                                    <Bug
                                        className={cn(
                                            'w-8 transition-[filter] duration-300',
                                            !debugMode && 'force-greyOut',
                                        )}
                                    />
                                    <span className="sr-only">{`${debugMode ? 'Disable' : 'Enable'} Debug Mode`}</span>
                                </button>
                                <button className="shrink-0" onClick={toggleHidePlaywright}>
                                    <img
                                        className={cn(
                                            'w-8 transition-[filter] duration-300',
                                            hidePlaywright && 'force-greyOut',
                                        )}
                                        src="/playwright_logo.svg"
                                        alt="Toggle Playwright"
                                    />
                                    <span className="sr-only">{`${hidePlaywright ? 'Show' : 'Hide'} Playwright selectors`}</span>
                                </button>
                            </div>
                        )}
                    </div>
                    <RunViewerSteps
                        stepsToRun={runData.story.steps}
                        stepResultsFromBack={runData.runAttempt?.steps ?? []}
                        stepResultsFromWebSocket={stepsFromSocket ?? []}
                        storyConfig={runData.story}
                        blocksConfig={runData.blocks}
                        debugMode={debugMode}
                        hidePlaywright={hidePlaywright}
                    />
                </div>
                {highlightedStepRank !== undefined ? (
                    <ActionBugs
                        stepResultsFromBack={runData.runAttempt?.steps ?? []}
                        stepResultsFromWebSocket={[]}
                        error={runData.result?.error}
                    />
                ) : (
                    runData?.run?.status === 'finished' && (
                        <span className="text-sm">Select a step to view details</span>
                    )
                )}
            </div>
        </StepContext.Provider>
    ) : null;

    const videoComponent = isVideoLoading ? (
        <div className="mx-auto h-full flex items-center gap-1 text-muted-foreground">
            <StatusIcon status={'queued'} />
            <p>The agent is now loading the replay video</p>
        </div>
    ) : (
        <div className="bg-slate-100 rounded-[6px] overflow-hidden h-full pt-[4px] px-[8px]">
            <Tabs items={tabItems} />
        </div>
    );

    return { videoComponent, setPlayHeadToAction, stepsComponent, cancelRun };
};

const inactivityLabels = {
    off: <div key="off">Inactive sections will play in full</div>,
    'fast-forward': <div key="fast-forward">Inactive sections will be fast-forwarded</div>,
    skip: <div key="skip">Inactive sections will be skipped</div>,
};

const InactivityStatusIndicator = ({ inactivity }: { inactivity: Inactivity }) => {
    const transRef = useSpringRef();
    const transitions = useTransition(inactivity, {
        ref: transRef,
        keys: null,
        from: {
            transform: inactivity === 'fast-forward' ? 'translate3d(0,100%,0)' : 'translate3d(0,-100%,0)',
        },
        enter: { transform: 'translate3d(0,0%,0)' },
        leave: {
            transform: inactivity === 'fast-forward' ? 'translate3d(0,-100%,0)' : 'translate3d(0,100%,0)',
        },
    });
    useEffect(() => {
        transRef.start();
    }, [inactivity]);

    return (
        <div
            className="overflow-hidden grid text-center text-xs md:text-body text-slate-500"
            style={{ clipPath: 'inset(0)' }}
        >
            {transitions((style, item) => {
                const label = inactivityLabels[item];
                return (
                    <animated.div className="row-start-1 row-end-1 col-start-1 col-end-1" style={style}>
                        {label}
                    </animated.div>
                );
            })}
        </div>
    );
};
